import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Radio, RefreshCw, Loader2, Trophy, Timer, Monitor,
  ExternalLink, Facebook, Play, Pause, ArrowLeft, Tv, ChevronRight, Youtube
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import LiveClock from "@/components/common/LiveClock";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};


// Helper function to extract Facebook video URL for embedding
const getFacebookEmbedUrl = (fbLink) => {
  if (!fbLink) return null;
  const encodedUrl = encodeURIComponent(fbLink);
  return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=560&autoplay=true&mute=true`;
};

// Helper function to extract YouTube video ID and get embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  
  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1`;
    }
  }
  
  return null;
};

// Helper function to detect video platform
const getVideoEmbed = (url) => {
  if (!url) return { type: null, embedUrl: null };
  
  const lowerUrl = url.toLowerCase();
  
  // Check if it's YouTube
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return { type: 'youtube', embedUrl: getYouTubeEmbedUrl(url) };
  }
  
  // Check if it's Facebook
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.watch') || lowerUrl.includes('fb.com')) {
    return { type: 'facebook', embedUrl: getFacebookEmbedUrl(url) };
  }
  
  // Unknown platform
  return { type: 'unknown', embedUrl: null };
};

// Individual match card for the grid with embedded video
function LiveMatchCard({ match, tournament }) {
  if (!match) {
    return (
      <div className="bg-gray-900 rounded-2xl border border-gray-800 flex items-center justify-center h-full min-h-[350px]">
        <div className="text-center text-gray-600">
          <Monitor className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Esperando transmisión</p>
        </div>
      </div>
    );
  }

  const videoEmbed = getVideoEmbed(match.facebook_link);
  const hasValidEmbed = videoEmbed.embedUrl !== null;

  return (
    <div className={`bg-gray-900 rounded-2xl border-2 overflow-hidden transition-all flex flex-col ${
      match.status === 'live' ? 'border-red-500/50 shadow-lg shadow-red-500/20' : 'border-gray-800'
    }`}>
      {/* Tournament Header */}
      <div 
        className="px-4 py-2 flex items-center justify-between flex-shrink-0"
        style={{ backgroundColor: tournament?.color || '#10B981' }}
      >
        <div className="flex items-center gap-2 text-white">
          <Trophy className="h-4 w-4" />
          <span className="text-sm font-bold truncate">{tournament?.name || 'Torneo'}</span>
          <span className="text-xs opacity-75">{tournament?.season}</span>
        </div>
        {match.status === 'live' && (
          <Badge className="bg-red-500 text-white text-xs animate-pulse border-0">
            <Radio className="h-3 w-3 mr-1" />
            EN VIVO
          </Badge>
        )}
      </div>

      {/* Teams and Score Bar */}
      <div className="px-4 py-3 bg-gray-800 flex items-center justify-between flex-shrink-0">
        {/* Home Team */}
        <div className="flex items-center gap-2 flex-1">
          {match.home_team?.logo_url && (
            <img 
              src={getImageUrl(match.home_team.logo_url)} 
              alt="" 
              className="w-10 h-10 object-contain"
            />
          )}
          <div className="min-w-0">
            <span className="text-white text-sm font-bold block truncate">
              {match.home_team?.name}
            </span>
          </div>
        </div>

        {/* Score */}
        <div className="flex flex-col items-center px-4">
          <div className="text-3xl font-black text-white">
            {match.home_score ?? 0} - {match.away_score ?? 0}
          </div>
          {match.status === 'live' && (
            <div className="mt-1">
              <LiveClock match={match} size="md" variant="dark" />
            </div>
          )}
        </div>

        {/* Away Team */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <div className="min-w-0 text-right">
            <span className="text-white text-sm font-bold block truncate">
              {match.away_team?.name}
            </span>
          </div>
          {match.away_team?.logo_url && (
            <img 
              src={getImageUrl(match.away_team.logo_url)} 
              alt="" 
              className="w-10 h-10 object-contain"
            />
          )}
        </div>
      </div>

      {/* Video Embed Area (Facebook or YouTube) */}
      <div className="flex-1 bg-black relative min-h-[220px]">
        {hasValidEmbed ? (
          <iframe
            src={videoEmbed.embedUrl}
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen={true}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; accelerometer; gyroscope"
            title={`${match.home_team?.name} vs ${match.away_team?.name}`}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-gray-900 to-gray-800">
            <Tv className="h-16 w-16 mb-3 opacity-30" />
            <p className="text-sm font-medium">Transmisión no disponible</p>
            <p className="text-xs mt-1 opacity-60">
              {match.facebook_link ? 'El enlace de video no es compatible' : 'El enlace de video no está configurado'}
            </p>
          </div>
        )}
      </div>

      {/* Footer with platform indicator */}
      <div className="px-4 py-2 bg-gray-800/80 flex items-center justify-between flex-shrink-0">
        <Link
          to={`/deportes/partido/${match.match_id}`}
          className="flex items-center gap-1 text-xs text-white hover:text-primary transition-colors font-medium"
        >
          Ver detalles y fotos
          <ChevronRight className="h-3 w-3" />
        </Link>
        {match.facebook_link && (
          <a
            href={match.facebook_link}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1 text-xs transition-colors ${
              videoEmbed.type === 'youtube' 
                ? 'text-red-400 hover:text-red-300' 
                : 'text-blue-400 hover:text-blue-300'
            }`}
          >
            {videoEmbed.type === 'youtube' ? (
              <>
                <Youtube className="h-3 w-3" />
                YouTube
              </>
            ) : (
              <>
                <Facebook className="h-3 w-3" />
                Facebook
              </>
            )}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function TransmisionesEnVivoPage() {
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [matchesRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/sports/matches?status=live&limit=100`),
        axios.get(`${API}/sports/tournaments`),
      ]);
      
      setMatches(matchesRes.data.items || []);
      setTournaments(tournamentsRes.data.items || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const getTournamentById = (id) => tournaments.find(t => t.tournament_id === id);

  // Fill array to always have 9 slots (3x3 grid)
  const gridMatches = [...matches];
  while (gridMatches.length < 9) {
    gridMatches.push(null);
  }

  return (
    <Layout>
      <PageHero
        title="Transmisiones en Vivo"
        subtitle="Sigue todos los partidos en tiempo real"
        icon={<Radio className="h-8 w-8" />}
        gradient="from-red-600 to-orange-500"
      />

      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          {/* Header with controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Tv className="h-8 w-8 text-red-500" />
                {matches.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full animate-pulse flex items-center justify-center">
                    <span className="text-[10px] text-white font-bold">{matches.length}</span>
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {matches.length > 0 
                    ? `${matches.length} Partido${matches.length > 1 ? 's' : ''} en Vivo`
                    : 'No hay partidos en vivo'
                  }
                </h2>
                <p className="text-sm text-gray-500">
                  {lastUpdate && `Actualizado: ${lastUpdate.toLocaleTimeString('es-HN')}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Auto Refresh Toggle */}
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="gap-2"
              >
                {autoRefresh ? (
                  <>
                    <Pause className="h-4 w-4" />
                    Auto (30s)
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Manual
                  </>
                )}
              </Button>

              {/* Manual Refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={loading}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>

              {/* Back to Sports */}
              <Link to="/deportes">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Deportes
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Bar */}
          {matches.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl mb-8 border border-red-100">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
                <span className="font-bold text-lg text-gray-800">{matches.length}</span>
                <span className="text-gray-600">Partidos en vivo</span>
              </div>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <Facebook className="h-4 w-4 text-blue-600" />
                <span className="font-bold text-gray-800">{matches.filter(m => m.facebook_link).length}</span>
                <span className="text-gray-600">Con transmisión</span>
              </div>
            </div>
          )}

          {/* Main Grid - 3x3 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-red-500" />
            </div>
          ) : matches.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gridMatches.slice(0, 9).map((match, index) => (
                  <div key={match?.match_id || `empty-${index}`} className="h-[420px]">
                    <LiveMatchCard
                      match={match}
                      tournament={match ? getTournamentById(match.tournament_id) : null}
                    />
                  </div>
                ))}
              </div>

              {/* Additional matches if more than 9 */}
              {matches.length > 9 && (
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <Radio className="h-5 w-5 text-red-500" />
                    Más partidos en vivo ({matches.length - 9})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {matches.slice(9).map((match) => (
                      <div key={match.match_id} className="h-[420px]">
                        <LiveMatchCard
                          match={match}
                          tournament={getTournamentById(match.tournament_id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Empty State */
            <div className="text-center py-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl">
              <div className="relative inline-block mb-6">
                <Tv className="h-24 w-24 text-gray-300" />
                <div className="absolute -top-2 -right-2 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <Radio className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-500 mb-3">No hay partidos en vivo</h3>
              <p className="text-gray-400 max-w-md mx-auto mb-6">
                Cuando los partidos estén en transmisión aparecerán aquí automáticamente.
                La página se actualiza cada 30 segundos.
              </p>
              <Link to="/deportes">
                <Button variant="outline" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Ver todos los partidos
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
